import { Users, Crown, MessageSquare, Layout, FolderKanban, FileText, DollarSign, TrendingUp, RefreshCw } from 'lucide-react'
import { SupabaseClient } from '@supabase/supabase-js'
import { useWebbMetrics } from '@/lib/data/webb'
import { HexLoader } from '@/components/ui/HexLoader'

// Price map for MRR estimation (base monthly prices)
const PLAN_PRICES: Record<string, number> = {
  investigator: 20,
  researcher: 35,
  analyst: 80,
}

interface WebbOverviewTabProps {
  client: SupabaseClient | null
}

export function WebbOverviewTab({ client }: WebbOverviewTabProps) {
  const { metrics, isLoading, isRefreshing, error, refetch } = useWebbMetrics(client)

  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-[var(--text-muted)]">
        <Layout size={48} className="mb-4 opacity-20" />
        <p className="text-lg font-medium">Not Connected</p>
        <p className="text-sm">Configure Supabase credentials to view Webb metrics</p>
      </div>
    )
  }

  if (isLoading && metrics.totalUsers === 0) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <HexLoader size="lg" text="Loading Webb metrics" />
      </div>
    )
  }

  if (error && metrics.totalUsers === 0) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-4 rounded-lg">
        {error}
      </div>
    )
  }

  const freeUsers = metrics.totalUsers - metrics.paidUsers
  const conversionRate = metrics.totalUsers > 0 ? ((metrics.paidUsers / metrics.totalUsers) * 100).toFixed(1) : '0.0'

  // Estimated MRR from active paid subscribers
  const estimatedMRR = Object.entries(metrics.monthlyRevenue).reduce((sum, [slug, count]) => {
    const price = PLAN_PRICES[slug.toLowerCase()] ?? 0
    return sum + price * count
  }, 0)
  const estimatedARR = estimatedMRR * 12

  const tierEntries = Object.entries(metrics.tierBreakdown).sort((a, b) => b[1] - a[1])
  const planEntries = Object.entries(metrics.planBreakdown).sort((a, b) => b[1] - a[1])
  const variantEntries = Object.entries(metrics.variantBreakdown).sort((a, b) => b[1] - a[1])

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${metrics.totalUsers > 0 ? 'bg-emerald-500' : 'bg-yellow-500'}`} />
          <span className="text-sm text-[var(--text-muted)]">
            {metrics.totalUsers > 0 ? 'Data loaded' : 'Connecting...'}
          </span>
          {isRefreshing && (
            <span className="flex items-center gap-1 text-sm text-[var(--text-muted)]">
              <RefreshCw className="w-3 h-3 animate-spin" /> Refreshing...
            </span>
          )}
        </div>
        <button
          onClick={() => refetch(true)}
          disabled={isRefreshing}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--bg-element)] text-[var(--text-muted)] rounded-lg hover:bg-[var(--bg-hover)] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-3">
        <StatCard icon={Users} label="Total Users" value={metrics.totalUsers.toLocaleString()} subtext={`${metrics.newUsersLast7d} new this week`} />
        <StatCard icon={Crown} label="Paid Users" value={metrics.paidUsers.toLocaleString()} subtext={`${conversionRate}% conversion`} highlight />
        <StatCard icon={Users} label="Free Users" value={freeUsers.toLocaleString()} subtext={`${metrics.totalUsers > 0 ? ((freeUsers / metrics.totalUsers) * 100).toFixed(1) : 0}% of total`} />
        <StatCard icon={MessageSquare} label="Total Messages" value={metrics.totalMessagesSent.toLocaleString()} subtext={`${metrics.avgMessagesPerUser} avg/user`} />
        <StatCard icon={Layout} label="Canvases" value={metrics.totalCanvases.toLocaleString()} subtext="User-created" />
        <StatCard icon={DollarSign} label="Est. MRR" value={`$${estimatedMRR.toLocaleString()}`} subtext={`$${estimatedARR.toLocaleString()} ARR`} color="green" />
      </div>

      {/* Tier Breakdown & Revenue */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Tier Breakdown */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-400" />
            Subscription Tiers
          </h3>
          <div className="space-y-4">
            {/* Tier cards */}
            <div className="grid grid-cols-2 gap-2">
              {tierEntries.filter(([t]) => t.toLowerCase() !== 'free').map(([tier, count]) => {
                const pct = metrics.totalUsers > 0 ? ((count / metrics.totalUsers) * 100).toFixed(1) : '0'
                return (
                  <div key={tier} className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                    <div className="text-xs text-amber-400 mb-1 capitalize">{tier}</div>
                    <div className="text-xl font-bold text-[var(--text-main)]">{count.toLocaleString()}</div>
                    <div className="text-xs text-[var(--text-muted)]">{pct}% of users</div>
                  </div>
                )
              })}
            </div>

            {/* Free users */}
            <div className="flex items-center justify-between p-2 rounded bg-[var(--bg-element)]">
              <span className="text-xs font-medium text-[var(--text-muted)]">Free Users</span>
              <span className="text-sm font-bold text-[var(--text-main)]">{freeUsers.toLocaleString()}</span>
            </div>

            {/* Conversion Rate */}
            <div className="flex items-center justify-between pt-2 border-t border-[var(--border)]">
              <span className="text-xs text-[var(--text-muted)]">Conversion Rate</span>
              <span className="text-sm font-bold text-[var(--primary)]">{conversionRate}%</span>
            </div>

            {/* Billing variants */}
            {variantEntries.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-[var(--border)]">
                <div className="text-xs font-medium text-[var(--text-muted)] mb-2">Billing Variants</div>
                {variantEntries.map(([variant, count]) => (
                  <div key={variant} className="flex items-center justify-between text-xs">
                    <span className="text-[var(--text-muted)] capitalize">{variant}</span>
                    <span className="font-semibold text-[var(--text-main)]">{count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Revenue & Engagement */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <h3 className="text-base font-semibold text-[var(--text-main)] mb-4 flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-400" />
            Revenue & Engagement
          </h3>
          <div className="space-y-4">
            {/* MRR & ARR */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="text-xs text-emerald-400 mb-1">Est. MRR</div>
                <div className="text-xl font-bold text-emerald-400">${estimatedMRR.toLocaleString()}</div>
                <div className="text-xs text-[var(--text-muted)]">{metrics.activeSubscriptions} active subs</div>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div className="text-xs text-emerald-400 mb-1">Est. ARR</div>
                <div className="text-xl font-bold text-emerald-400">${estimatedARR.toLocaleString()}</div>
                <div className="text-xs text-[var(--text-muted)]">{metrics.canceledSubscriptions} canceled</div>
              </div>
            </div>

            {/* Revenue by plan */}
            {planEntries.filter(([s]) => s.toLowerCase() !== 'free').length > 0 && (
              <div className="space-y-2">
                {planEntries.filter(([s]) => s.toLowerCase() !== 'free').map(([slug, count]) => {
                  const price = PLAN_PRICES[slug.toLowerCase()] ?? 0
                  const revenue = price * (metrics.monthlyRevenue[slug] ?? 0)
                  return (
                    <div key={slug} className="flex items-center justify-between p-2 rounded bg-[var(--bg-element)]">
                      <div className="text-xs">
                        <span className="text-[var(--text-main)] capitalize">{slug}</span>
                        {price > 0 && <span className="text-[var(--text-muted)] ml-1">@ ${price}/mo</span>}
                        <span className="text-[var(--text-muted)] ml-1">({count})</span>
                      </div>
                      {revenue > 0 && (
                        <span className="text-sm font-semibold text-[var(--text-main)]">${revenue.toLocaleString()}/mo</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {/* Message engagement */}
            <div className="space-y-2 pt-2 border-t border-[var(--border)]">
              <div className="text-xs font-medium text-[var(--text-muted)] mb-2">Message Engagement</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">Avg per user (all)</span>
                <span className="font-semibold text-[var(--text-main)]">{metrics.avgMessagesPerUser.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">Avg per paid user</span>
                <span className="font-semibold text-amber-400">{metrics.avgMessagesPerPaidUser.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">Avg per free user</span>
                <span className="font-semibold text-[var(--text-main)]">{metrics.avgMessagesPerFreeUser.toLocaleString()}</span>
              </div>
            </div>

            {/* Platform stats */}
            <div className="space-y-2 pt-2 border-t border-[var(--border)]">
              <div className="text-xs font-medium text-[var(--text-muted)] mb-2">Platform</div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">Projects / Datasets</span>
                <span className="font-semibold text-[var(--text-main)]">{metrics.totalProjects.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">Documents uploaded</span>
                <span className="font-semibold text-[var(--text-main)]">{metrics.totalDocuments.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">New users (24h)</span>
                <span className="font-semibold text-cyan-400">{metrics.newUsersLast24h.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--text-muted)]">New users (7d)</span>
                <span className="font-semibold text-cyan-400">{metrics.newUsersLast7d.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

interface StatCardProps {
  icon: React.ElementType
  label: string
  value: string
  subtext: string
  highlight?: boolean
  color?: 'default' | 'green'
}

function StatCard({ icon: Icon, label, value, subtext, highlight, color = 'default' }: StatCardProps) {
  return (
    <div className={`bg-[var(--bg-card)] border p-4 rounded-xl ${
      highlight ? 'border-amber-500/20' : color === 'green' ? 'border-emerald-500/20' : 'border-[var(--border)]'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <Icon className={`h-5 w-5 ${
          highlight ? 'text-amber-400' : color === 'green' ? 'text-emerald-400' : 'text-[var(--primary)]'
        }`} />
        <span className="text-[var(--text-muted)] text-sm">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${
        highlight ? 'text-amber-400' : color === 'green' ? 'text-emerald-400' : 'text-[var(--text-main)]'
      }`}>
        {value}
      </p>
      <p className="text-[var(--text-muted)] text-xs mt-1">{subtext}</p>
    </div>
  )
}
